/* eslint-disable max-len */
import { IAnonymousStudentService } from '@studyportals/anonymous-student-interfaces';
import { ISessionService, IWishlistService } from '@studyportals/student-interfaces';
import { FavouriteAdded, FavouriteRemoved } from '@studyportals/wishlist-service-core';
import { CIOObjectType } from './enums/CIOObjectType';
import { IStudy, IOrganisation } from '@studyportals/datalayer';
import { countryGetIsoCode } from '@studyportals/static-domain-data/countries/country-get-iso-code';
import { subdisciplineGetParentId } from '@studyportals/static-domain-data/disciplines/discipline-get-parent-discipline';
import { IStudent, StudentField } from '@studyportals/studentdomain';
import { CIORelationshipAction } from './enums/CIORelationshipAction';
import { IDiscipline, IOrganisationStudy } from '@studyportals/datalayer/dist/src/types';
import { CIOTrackEventType } from './enums/CIOTrackEventType';
import { CampaignType } from './enums/CampgainType';
import { CurrentEligibilityScholarshipUpdateDTO } from '@studyportals/eligibility-model';
import EligibilityModelUtils from '../EligibilityModel/EligibilityModelUtils';

export default class CIOTracker {
	constructor(
		private anonymousStudent: IAnonymousStudentService,
		private sessionService: ISessionService,
		private wishlistService: IWishlistService
	) {}

	private identifyUser(id: string, email: string): void {
		window._cio?.identify({
			id,
			email
		});
	}

	private track(eventType: CIOTrackEventType, data?: Record<string, unknown>): void {
		data = {
			...data,
			custom_timestamp: Math.round(Date.now() / 1000)
		};

		window._cio?.track(eventType, data);
	}

	private async updateRelationships(
		action: CIORelationshipAction,
		objectType: CIOObjectType,
		ids: string[]
	): Promise<void> {
		const session = await this.sessionService?.getSession();

		if (session) {
			const identityId = session?.getUser().identityId;
			const email = session?.getUser().email;

			const relationships = [];
			for (const id of ids) {
				relationships.push({
					identifiers: {
						object_type_id: objectType,
						object_id: id
					}
				});
			}

			if (relationships.length === 0) return;

			window._cio?.identify({
				id: identityId,
				email,
				cio_relationships: {
					action,
					relationships
				}
			});
		}
	}

	private getCampaignType(campaignType: number): CampaignType {
		switch (campaignType) {
			case 0:
				return CampaignType.HYBRID_EBP;
			case 1:
				return CampaignType.FULL_EBP;
			case 2:
				return CampaignType.RBP;
			case 3:
				return CampaignType.FREE_TRIAL;
			case 4:
				return CampaignType.NRBP;
			case 5:
				return CampaignType.ADVANCED_NRBP;
		}

		return CampaignType.BASIC;
	}

	public resetCIOIdentity(): void {
		if (!window._cio) return;
		if (!window._cio.reset) return;

		window._cio.reset();
	}

	public async handleUserIdentification(isLoggingIn = false): Promise<void> {
		const session = await this.sessionService?.getSession();

		if (session) {
			const email = session?.getUser()?.email;
			const identityId = session?.getUser()?.identityId;

			if (email && identityId) {
				this.identifyUser(identityId, email);
			}

			if (isLoggingIn) {
				this.track(CIOTrackEventType.AUTOMATION_UPDATE_SUBS);
			}
		}
	}

	public async syncInitialInterests(): Promise<void> {
		const studentInterests = await this.anonymousStudent?.getStudentData([
			StudentField.INTERESTS_DISCIPLINES,
			StudentField.INTERESTS_COUNTRIES
		]);

		const disciplines = studentInterests?.[StudentField.INTERESTS_DISCIPLINES] || [];
		const countries = studentInterests?.[StudentField.INTERESTS_COUNTRIES] || [];

		if (disciplines.length) {
			const disciplinesMapped = disciplines.map((discipline) => discipline.toString());
			await this.updateRelationships(CIORelationshipAction.ADD, CIOObjectType.DISCIPLINE, disciplinesMapped);
		}

		if (countries.length) {
			const countriesMapped: string[] = [];

			for (const country of countries) {
				const iso = countryGetIsoCode(country.toString());
				if (iso) {
					countriesMapped.push(iso);
				}
			}

			void this.updateRelationships(CIORelationshipAction.ADD, CIOObjectType.COUNTRY, countriesMapped);
		}
	}

	public async syncFavourites(): Promise<void> {
		const studyIds: string[] = [];

		const wishlist = await this.wishlistService.getWishlist();

		const favourites = wishlist.favourites.filter((favourite) => favourite.study?.card?.isPublic());

		studyIds.push(...favourites.map((favourite) => favourite.study.id.toString()));

		void this.updateRelationships(CIORelationshipAction.ADD, CIOObjectType.STUDY, studyIds);
	}

	public trackFavouriteAdded(event: FavouriteAdded): void {
		void this.updateRelationships(CIORelationshipAction.ADD, CIOObjectType.STUDY, [event.studyId.toString()]);

		this.track(CIOTrackEventType.FAVOURITE_ADDED, {
			study_id: event.studyId.toString()
		});
	}

	public trackFavouriteRemoved(event: FavouriteRemoved): void {
		void this.updateRelationships(CIORelationshipAction.REMOVE, CIOObjectType.STUDY, [event.studyId.toString()]);

		this.track(CIOTrackEventType.FAVOURITE_REMOVED, {
			study_id: event.studyId.toString()
		});
	}

	public trackUpdatedInterests(changes: IStudent): void {
		if (changes[StudentField.INTERESTS_COUNTRIES]) {
			const countries: string[] = [];

			for (const country of changes[StudentField.INTERESTS_COUNTRIES]) {
				const iso = countryGetIsoCode(country.toString());
				if (iso) {
					countries.push(iso);
				}
			}

			void this.updateRelationships(CIORelationshipAction.ADD, CIOObjectType.COUNTRY, countries);
		}

		if (changes[StudentField.INTERESTS_DISCIPLINES]) {
			const disciplines: string[] = [];

			for (const discipline of changes[StudentField.INTERESTS_DISCIPLINES]) {
				disciplines.push(discipline.toString());
			}

			void this.updateRelationships(CIORelationshipAction.ADD, CIOObjectType.DISCIPLINE, disciplines);
		}
	}

	public trackStudyPageEvents(study: IStudy): void {
		const disciplines = study.disciplines.map((discipline: IDiscipline) => discipline.id);

		disciplines.forEach((disciplineId: string) => {
			const parentDisciplineId = subdisciplineGetParentId(disciplineId);
			const isPresentInArray = disciplines.includes(parentDisciplineId ?? '');

			if (parentDisciplineId && !isPresentInArray) {
				disciplines.push(parentDisciplineId);
			}
		});

		const campaignType = this.getCampaignType(study.campaignType ?? -1);
		const organisation = study.organisation as IOrganisationStudy;
		const countries = organisation.locations?.map((location) => location.countryIso.toUpperCase());

		this.track(CIOTrackEventType.STUDY_VIEW, {
			study_id: study.studyId,
			disciplines,
			countries,
			organisation_id: organisation.id.toString(),
			campaign_type: campaignType
		});
	}

	public trackOrganisationPageEvents(organisation: IOrganisation): void {
		this.track(CIOTrackEventType.ORG_VIEW, {
			organisation_id: organisation.organisationId
		});
	}

	public trackScholarshipEligibilityEvent(eligibility: CurrentEligibilityScholarshipUpdateDTO): void {
		if (!eligibility) return;
		const eligibilityModelUtils = new EligibilityModelUtils();

		const eligibilityUpdateToTrackArray = eligibilityModelUtils.updatedEligibilityItems(eligibility);

		if (!eligibilityUpdateToTrackArray || !eligibilityUpdateToTrackArray.length) return;

		const eligibilityUpdateToTrack: Record<string, boolean> = {};

		for (const key of eligibilityUpdateToTrackArray) {
			eligibilityUpdateToTrack[key] = true;
		}

		this.track(CIOTrackEventType.SCHOLARSHIP_ELIGIBILITY, {
			...eligibilityUpdateToTrack
		});
	}
}
